import { createSlice } from '@reduxjs/toolkit'

import {
  checkLeadExists,
  createCallLog,
  createLeadNote,
  downloadDocument,
  getLeadAppointment,
  getLeadDocuments,
  getLeadLostReasons,
  getPendingDocuments,
  getLeads,
  getLeadDetail,
  getLeadInvalidReasons,
  getLeadActivities,
  getLeadTasks,
  updateLeadAppointmentStatus,
  updateLeadNote,
  updateLostReason,
  uploadDocument,
  deleteLeadNote,
  updateCallLog,
} from 'services/lead'

import {
  GetLeadsResponse,
  LeadAddressResponse,
  LeadDetailResponse,
  LeadInvalidLostResponse,
  GetLeadAppointmentResponse,
  LeadActivitiesResponse,
  LeadTasksResponse,
  LeadTask as SingleLeadTask,
  GetLeadDocumentsResponse,
  GetPendingDocumentsResponse,
  GetLeadActivitiesPayload,
  MergeableLeadResponse,
  MergeableLeadData,
  GetLeadSearchResponse,
} from 'services/types/lead'

import { formatContactNumber, formatContactNumberDisplayValue } from 'utils/generic'
import { getDetail } from 'services/lead-detail'
import { GetLeadContactsResponse } from 'services/types/lead-contact'
import { ContactInfo, LeadContacts, LeadInfoSearch } from 'store/types/lead'

import { uuidv4 } from 'utils/generic'
import { LeadInfo } from 'types/store'
import { formatAddress } from 'utils/address'
import { parseDate } from 'utils/datetime'
import { WindowProps } from 'components/Window/types'
import { updateLiveCallLog } from 'services/liveCall'
import { unSet, setLoading, setSuccess } from 'api/remoteData'
import { FETCHING_STATUS, LEAD_APPOINTMENT_STATUS, LEAD_MEETING_MINUTE_EMAIL_STATUS } from 'utils/constants'
import {
  Priority,
  Status,
  CustomerType,
  LeadType,
  LeadAppointmentStatusId,
  LeadDetails,
  LeadActivityType,
} from 'types/global'

import { FormValues as QuotationFormValues } from 'modules/Quotation/Form/types'

import {
  LeadActivities,
  LeadAppointment,
  LeadDetail,
  LeadDetailQuicklinkOptionValue,
  LeadDetailWindows,
  LeadDetailWindowType,
  LeadDocuments,
  LeadInvalidLostReason,
  LeadNote,
  LeadTask,
  LeadTasks,
  LeadUploadDocuments,
  LeadCallLogActivity,
  LeadCallLogFromTask,
  LiveCallLog,
  UploadWindowItem,
  CallLog,
  EmailComposer,
} from './types/lead'

import { PayloadAction, LeadState } from './types'
import { mapCustomFieldLeadDetail } from './settings'
import { DeleteAttachmentsPayload } from 'services/lead-email'

const initialState: LeadState = {
  leadAppointment: unSet(),
  leadCallLog: undefined,
  leadContacts: unSet(),
  leadDetailWindows: { appointment: undefined, note: undefined, upload: undefined, email: undefined },
  leadDownloadDocument: unSet(),
  leadExportData: unSet(),
  leadNote: undefined,
  selectedLeadSource: undefined,
  selectedLeadCampaign: undefined,
  leadLostReasons: unSet(),
  leadInvalidReasons: unSet(),
  leadStatistics: unSet(),
  leads: unSet(),
  leadExists: false,
  leadAddresses: unSet(),
  leadDetail: unSet(),
  leadActivities: unSet(),
  leadTasks: {
    page: unSet(),
    data: undefined,
  },
  leadDocuments: unSet(),
  liveCallLog: undefined,
  leadDuplicate: undefined,
}

type WindowValues =
  | LeadCallLogActivity
  | LeadAppointment
  | LeadUploadDocuments
  | LeadNote
  | LiveCallLog
  | EmailComposer
  | LeadTask

type LeadDetailWindowData = {
  initialValues?: WindowValues
  leadId: string
  referenceId?: string
}

export type LeadWindowPayload = {
  data: LeadDetailWindowData
  type: LeadDetailWindowType
  windowState?: WindowProps<LeadDetailWindowType>['windowState']
  isPriority?: boolean
  mode?: 'create' | 'update'
}

const { reducer, actions } = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearInvalidLeadReasons: (state) => ({ ...state, leadInvalidReasons: initialState.leadInvalidReasons }),
    clearLeadContacts: (state) => ({ ...state, leadContacts: unSet() }),
    addLeadDetailWindow: (state, { payload }: { payload: LeadWindowPayload }) => {
      const {
        data: { initialValues, leadId, referenceId },
        type,
        windowState = 'active',
        isPriority = false,
        mode,
      } = payload

      if (state.leadDetailWindows[type] && windowState != 'active') {
        return state
      }

      const { title, ...otherConfig } = WINDOW_CONFIG[type]

      const defaultNewLeadDetailWindow: WindowProps<LeadDetailWindowType> = {
        ...otherConfig,
        isMinimized: false,
        isPadded: false,
        title: referenceId ? title.update : title.create,
        type,
        windowId: uuidv4(),
        windowState,
      }

      const otherWindows = minimizeOtherLeadDetailWindows({ ...state.leadDetailWindows }, type)

      if (type === 'upload') {
        return {
          ...state,
          leadDetailWindows: {
            ...otherWindows,
            upload: {
              ...defaultNewLeadDetailWindow,
              canClose: false,
              data: {
                ...initialValues,
              },
              minimizeIcon: 'chevron-up',
              restoreIcon: 'chevron-down',
            },
          },
        }
      }

      const { emptyValue, relatedStore } = LeadDetailWindowInfo[type]
      const defaultValues = initialValues ?? emptyValue
      const relatedStoreValues =
        type === 'appointment' && defaultValues ? setSuccess(defaultValues) : defaultValues || undefined

      return {
        ...state,
        [relatedStore]: relatedStoreValues,
        leadDetailWindows: {
          ...otherWindows,
          [type]: {
            ...defaultNewLeadDetailWindow,
            data: {
              ...defaultValues,
              leadId,
              [LeadDetailWindowInfo[type].referenceIdField]: referenceId,
            },
            isPriority,
            mode,
          },
        },
      }
    },
    clearLeadAppointment: (state) => ({ ...state, leadAppointment: initialState.leadAppointment }),
    clearLeadDetailWindows: (state) => ({
      ...state,
      leadAppointment: initialState.leadAppointment,
      leadDetailWindows: initialState.leadDetailWindows,
    }),
    closeLeadDetailWindow: (state, { payload }: { payload: { type: LeadDetailWindowType; windowId: string } }) => {
      const { type } = payload

      const storageKey: LeadDetailWindowType = type === 'message' ? 'call' : type

      const tempState: LeadState = {
        ...state,
        leadDetailWindows: {
          ...state.leadDetailWindows,
          [storageKey]: initialState.leadDetailWindows[storageKey],
        },
      }

      switch (type) {
        case 'appointment':
          tempState.leadAppointment = initialState.leadAppointment
          break
        case 'call':
        case 'message':
        case 'task':
          tempState.leadCallLog = initialState.leadCallLog
          break
        case 'liveCall':
          tempState.liveCallLog = initialState.liveCallLog
          break
        case 'note':
          tempState.leadNote = initialState.leadNote
          break
        case 'quotation':
          tempState.quotation = initialState.quotation
          break
        case 'upload':
          tempState.uploadDocuments = initialState.uploadDocuments
          break
        case 'email':
          tempState.emailComposer = initialState.emailComposer
          break
        default:
      }

      let hasOpenWindow = false

      Object.keys(tempState.leadDetailWindows).forEach((k) => {
        if (hasOpenWindow) {
          return
        }

        const key = k as LeadDetailWindowType
        hasOpenWindow = !hasOpenWindow && key !== type
      })

      return {
        ...tempState,
        leadDetailWindows: hasOpenWindow ? tempState.leadDetailWindows : initialState.leadDetailWindows,
      }
    },
    clearNewLeadActivity: (state) => {
      if (state.leadActivities.status !== FETCHING_STATUS.success) {
        return { ...state }
      }

      return {
        ...state,
        leadActivities: {
          ...state.leadActivities,
          data: {
            ...state.leadActivities.data,
            newActivity: undefined,
          },
        },
      }
    },
    updateLeadDetailWindows: (
      state,
      {
        payload,
      }: {
        payload: WindowProps<LeadDetailWindowType> & {
          data: LeadDetailWindowData
        }
      },
    ) => {
      const { isMinimized, type } = payload
      const storageKey: LeadDetailWindowType = type === 'message' ? 'call' : type

      const otherWindows = !isMinimized
        ? minimizeOtherLeadDetailWindows({ ...state.leadDetailWindows }, storageKey)
        : { ...state.leadDetailWindows }

      const windows = { ...state.leadDetailWindows }

      Object.keys(windows).forEach((k) => {
        const key = k as LeadDetailWindowType
        if (key === storageKey) {
          windows[key] = payload
        } else {
          windows[key] = otherWindows[key]
        }
      })

      return {
        ...state,
        leadDetailWindows: {
          ...windows,
        },
      }
    },
    setLeadMeetingMinuteId: (state, { payload }) => {
      if (state.leadAppointment.status !== FETCHING_STATUS.success) {
        return state
      }

      return {
        ...state,
        leadAppointment: {
          ...state.leadAppointment,
          data: { ...state.leadAppointment.data, leadMeetingMinutesId: payload },
        },
        leadDetailWindows: {
          ...state.leadDetailWindows,
          appointment: {
            ...state.leadDetailWindows.appointment,
            data: {
              ...state.leadDetailWindows.appointment.data,
              leadMeetingMinutesId: payload,
            },
          },
        },
      }
    },
    updateUploadWindow: (state, { payload }) => {
      const prevDocuments = state.leadDetailWindows.upload ? state.leadDetailWindows.upload.data.documents : []
      return {
        ...state,
        leadDetailWindows: {
          ...state.leadDetailWindows,
          upload: {
            ...state.leadDetailWindows.upload,
            data: {
              documents: [...prevDocuments, ...payload.documents],
              leadId: payload.leadId,
            },
          },
        },
      }
    },
    removeUploadWindowDocument: (state, { payload }) => {
      const prevDocuments = state.leadDetailWindows.upload ? state.leadDetailWindows.upload.data.documents : []
      return {
        ...state,
        leadDetailWindows: {
          ...state.leadDetailWindows,
          upload: {
            ...state.leadDetailWindows.upload,
            data: {
              documents: [
                ...prevDocuments.filter((i: UploadWindowItem) =>
                  payload.documentId
                    ? i.documentId !== payload.documentId
                    : i.uploadRequestId !== payload.uploadRequestId,
                ),
              ],
              leadId: payload.leadId,
            },
          },
        },
      }
    },
    updateLeadNote: (state, { payload }) => ({
      ...state,
      leadNote: payload,
    }),
    setSelectedLeadSource: (state, { payload }) => ({
      ...state,
      selectedLeadSource: payload.leadSource,
    }),
    selectedLeadCampaign: (state, { payload }) => ({
      ...state,
      selectedLeadSource: payload.leadCampaign,
    }),
    setLeadDetail: (state, { payload }) => {
      const leadDetail = state.leadDetail.status === 'SUCCESS' ? state.leadDetail.data : ({} as LeadDetail)

      switch (payload.type) {
        case 'CUSTOM_FIELD':
          return {
            ...state,
            leadDetail: setSuccess({
              ...leadDetail,
              customFields: payload.value,
            }),
          }
        case 'CAMS_USERNAME':
          return {
            ...state,
            leadDetail: setSuccess({
              ...leadDetail,
              camsUsername: payload.value,
            }),
          }
        case 'COMPANY_INFO':
          return {
            ...state,
            leadDetail: setSuccess({
              ...leadDetail,
              company: {
                annualRevenue: payload.values.annualRevenue || 0,
                id: payload.values.companyId,
                industryId: payload.values.industryId,
                isTaxable: payload.values.isTaxable,
                name: payload.values.name,
                numEmployees: payload.values.numEmployees || 0,
                registrationNumber: payload.values.registrationNumber,
                website: payload.values.website,
              },
            }),
          }
        case 'LEAD_CAMPAIGN':
          return {
            ...state,
            leadDetail: setSuccess({
              ...leadDetail,
              leadCampaignId: payload.value.value,
              leadCampaignName: payload.value.label,
              leadCampaignStatus: 'Active',
            }),
          }
        case 'LEAD_NAME':
          return { ...state, leadDetail: setSuccess({ ...leadDetail, leadName: payload.payload }) }
        case 'LEAD_SOURCE':
          return {
            ...state,
            leadDetail: setSuccess({
              ...leadDetail,
              leadCampaignId: '',
              leadCampaignName: '',
              leadSourceId: payload.value.value,
              leadSourceName: payload.value.label,
              leadSourceStatus: 'Active',
            }),
          }
        case 'LEAD_TYPE':
          return {
            ...state,
            leadDetail: setSuccess({
              ...leadDetail,
              leadTypeId: payload.payload.value as LeadType,
              leadTypeName: payload.payload.label,
            }),
          }
        case 'LEAD_PRIORITY':
          return {
            ...state,
            leadDetail: setSuccess({
              ...leadDetail,
              leadPriorityId: payload.payload.value,
              leadPriorityName: payload.payload.label,
            }),
          }
        case 'LEAD_OWNER':
          // Exclude selected lead owner from collaborators
          const additionalOwners = leadDetail.additionalOwners.filter(
            (i: Record<string, string>) => i.id !== payload.payload.owner?.id,
          )

          return {
            ...state,
            leadDetail: setSuccess({
              ...leadDetail,
              additionalOwners,
              owner: payload.payload.owner,
              ownerTeam: payload.payload.ownerTeam,
            }),
          }
        case 'LEAD_ADDITIONAL_OWNERS':
          return {
            ...state,
            leadDetail: setSuccess({
              ...leadDetail,
              additionalOwners: payload.payload,
            }),
          }
        case 'MARKET_POTENTIAL':
          return {
            ...state,
            leadDetail: setSuccess({
              ...leadDetail,
              marketPotential: payload.payload,
            }),
          }
        case 'LEAD_IN_DATE':
          return {
            ...state,
            leadDetail: setSuccess({
              ...leadDetail,
              leadInDate: payload.value,
            }),
          }
        case 'DESCRIPTION':
          return {
            ...state,
            leadDetail: setSuccess({
              ...leadDetail,
              description: payload.value,
            }),
          }
        default:
          return state
      }
    },
    setLeadTasks: (state, { payload }) => ({
      ...state,
      leadTasks: {
        page: state.leadTasks.page,
        data: payload.data,
      },
    }),
    clearSelected: () => initialState,
    clearLeadDetail: (state) => ({ ...state, leadDetail: unSet(), leadDetailWindows: initialState.leadDetailWindows }),
    clearLeadTasks: (state) => ({ ...state, leadTasks: initialState.leadTasks }),
    clearLeadDocuments: (state) => ({ ...state, leadDocuments: initialState.leadDocuments }),
    setLeadDuplicate: (state, { payload }) => ({
      ...state,
      leadDuplicate: { ...payload, list: [...(payload.exacts || []), ...(payload.potentials || [])] },
    }),
    clearLeadDuplicate: (state) => ({
      ...state,
      leadDuplicate: initialState.leadDuplicate,
    }),
    // Call Log form and window state
    openCallLogWindow: (
      state,
      { payload }: { payload: { data?: CallLog | LeadCallLogFromTask; leadId: string; contactInfo?: ContactInfo } },
    ) => {
      if (state.leadDetailWindows.call) {
        return state
      }

      const type = payload.data?.isMessage ? 'message' : 'call'

      const { title, icon, ...otherConfig } = WINDOW_CONFIG[type]

      return {
        ...state,
        leadDetailWindows: {
          ...minimizeOtherLeadDetailWindows({ ...state.leadDetailWindows }, type),
          call: {
            ...otherConfig,
            data: payload.data,
            contactInfo: payload.contactInfo,
            icon: icon,
            // window props
            isMinimized: false,
            isPadded: false,
            mode: payload?.data ? 'update' : 'create',
            title: payload?.data ? title.update : title.create,
            type,
            windowId: uuidv4(),
            windowState: payload?.data ? 'readonly' : 'active',
          },
        },
      }
    },
    updateCallLogWindowData: (state, { payload }: { payload: Omit<CallLog, 'callTime'> & { callTime: string } }) => ({
      ...state,
      leadDetailWindows: {
        ...state.leadDetailWindows,
        call: {
          ...state.leadDetailWindows.call,
          data: payload,
          windowState: 'active',
        },
      },
    }),

    updateMeetingWindowTitle: (state, { payload }: { payload: { subTitle: string } }) => ({
      ...state,
      leadDetailWindows: {
        ...state.leadDetailWindows,
        appointment: {
          ...state.leadDetailWindows.appointment,
          subTitle: payload?.subTitle,
        },
      },
    }),
    // Meeting form and window state
    openMeetingWindow: (
      state,
      {
        payload,
      }: { payload: { leadId: string; leadMeetingId?: string; leadMeetingStatusId?: LeadAppointmentStatusId } },
    ) => {
      if (state.leadDetailWindows.appointment) {
        return state
      }

      const type = 'appointment'
      const { title, ...otherConfig } = WINDOW_CONFIG[type]

      return {
        ...state,
        leadDetailWindows: {
          ...minimizeOtherLeadDetailWindows({ ...state.leadDetailWindows }, type),
          appointment: {
            ...otherConfig,
            data: {
              leadAppointmentId: payload?.leadMeetingId,
              leadId: payload.leadId,
            },
            // window props
            isMinimized: false,
            isPadded: false,
            mode: payload?.leadMeetingId ? 'update' : 'create',
            title: payload?.leadMeetingId ? title.update : title.create,
            type,
            windowId: uuidv4(),
            windowState: payload?.leadMeetingId
              ? payload.leadMeetingStatusId === LEAD_APPOINTMENT_STATUS.Canceled
                ? 'disabled'
                : 'readonly'
              : 'active',
          },
        },
      }
    },
    updateEmailFormData: (
      state,
      {
        payload,
      }: { payload: { isDirty: boolean; deleteEmailAttachmentsPayload?: DeleteAttachmentsPayload['attachments'] } },
    ) => ({
      ...state,
      isLeadEmailFormDataDirty: payload?.isDirty,
      deleteEmailAttachmentsPayload: payload?.deleteEmailAttachmentsPayload,
    }),
    updateMeetingWindowData: (state, { payload }: { payload: LeadAppointment }) => ({
      ...state,
      leadDetailWindows: {
        ...state.leadDetailWindows,
        appointment: {
          ...state.leadDetailWindows.appointment,
          data: payload,
        },
      },
    }),
    openQuotationWindow: (
      state,
      {
        payload,
      }: {
        payload: {
          leadId: string
          opportunityId?: string
          quotationId?: string
          isReadOnly: boolean
          isRetainedData?: boolean
          duplicateFromQuotationId?: string
        }
      },
    ) => {
      if (state.leadDetailWindows.quotation) {
        return state
      }

      const type = 'quotation'
      const { title, ...otherConfig } = WINDOW_CONFIG[type]

      return {
        ...state,
        leadDetailWindows: {
          ...minimizeOtherLeadDetailWindows({ ...state.leadDetailWindows }, type),
          quotation: {
            ...otherConfig,
            data: {
              ...state.leadDetailWindows.quotation?.data,
              quotationId: payload?.quotationId,
              leadId: payload.leadId,
              opportunityId: payload.opportunityId,
            },
            isMinimized: false,
            isPadded: false,
            mode: payload?.quotationId ? 'update' : 'create',
            title: payload?.quotationId ? title.update : title.create,
            type,
            windowId: uuidv4(),
            windowState: payload?.isReadOnly ? 'readonly' : 'active',
            isRetainedData: payload?.isRetainedData,
            duplicateFromQuotationId: payload?.duplicateFromQuotationId,
          },
        },
      }
    },
    updateQuotationWindowData: (state, { payload }: { payload: QuotationFormValues }) => ({
      ...state,
      leadDetailWindows: {
        ...state.leadDetailWindows,
        quotation: {
          ...state.leadDetailWindows.quotation,
          data: {
            ...payload,
            leadId: payload.lead_id,
            opportunityId: payload.opportunity_id,
            quotationId: payload.quotation_id,
          },
        },
      },
    }),
    updateMeetingWindowProps: (state, { payload }: { payload: WindowProps<LeadDetailQuicklinkOptionValue> }) => ({
      ...state,
      leadDetailWindows: {
        ...state.leadDetailWindows,
        appointment: {
          ...state.leadDetailWindows.appointment,
          ...payload,
        },
      },
    }),
    openQuotationEmailWindow: (
      state,
      {
        payload,
      }: {
        payload: {
          emailType: string
          data?: {
            quotationId?: string
            subject?: string
            body?: string
            pdfUrl?: string
            fileName?: string
            recipientToName?: Array<{ value: string; fullName: string }>
            recipientCcName?: Array<{ value: string; fullName: string }> | undefined
            fromName?: { value: string; fullName: string }
            templateName?: string | null
          }
        }
      },
    ) => {
      if (state.leadDetailWindows.quotationEmail) {
        return state
      }

      const type = 'email'
      const { title, ...otherConfig } = WINDOW_CONFIG[type]

      return {
        ...state,
        leadDetailWindows: {
          ...minimizeOtherLeadDetailWindows({ ...state.leadDetailWindows }, type),
          email: {
            ...otherConfig,
            data: {
              emailType: payload.emailType,
              emailInfo: {
                quotationId: payload.data?.quotationId,
                templateName: payload.data?.templateName || null,
              },
            },
            isMinimized: false,
            isPadded: false,
            mode: payload?.data?.quotationId ? 'update' : 'create',
            title: payload?.data?.quotationId ? title.update : title.create,
            type,
            windowId: uuidv4(),
            windowState: 'active',
          },
        },
      }
    },
  },
  extraReducers: (builder) => {
    /**
     * Get Leads
     */
    builder
      .addCase(getLeads.pending, (state, { meta }: PayloadAction) => ({
        ...state,
        leads: setLoading(meta.requestId),
      }))
      .addCase(getLeads.fulfilled, (state, { meta, payload }) => {
        if (payload.status === 'FAIL') {
          return {
            ...state,
            leads: payload,
          }
        }
        const leadsData = mapGetLeadResponse(payload.data)
        return {
          ...state,
          leads: setSuccess(leadsData, meta.requestId),
        }
      })

    /**
     * Check Lead Exists
     */
    builder.addCase(checkLeadExists.fulfilled, (state, { payload }) => {
      if (payload.status === 'SUCCESS') {
        return {
          ...state,
          leadExists: payload.data.exists,
        }
      }
    })

    builder
      .addCase(getLeadDetail.pending, (state, { meta }) => ({
        ...state,
        leadDetail: setLoading(meta.requestId),
      }))
      .addCase(getLeadDetail.fulfilled, (state, { payload, meta }) => {
        if (payload.status === 'SUCCESS') {
          return {
            ...state,
            leadDetail: setSuccess(mapLeadDetailResponse(payload.data), meta.requestId),
          }
        }
        return {
          ...state,
          leadDetail: payload,
        }
      })

    builder
      .addCase(getDetail.pending, (state, { meta }) => ({
        ...state,
        leadDetail: setLoading(meta.requestId),
      }))
      .addCase(getDetail.fulfilled, (state, { payload }) => {
        if (payload.status === 'FAIL') {
          return {
            ...state,
            leadDetail: payload,
          }
        }

        if (payload.status === 'SUCCESS') {
          return {
            ...state,
            leadDetail: setSuccess(mapLeadDetailResponse(payload.data.lead_detail)),
            leadAddresses: setSuccess(mapLeadAddress(payload.data.lead_addresses)),
            leadContacts: setSuccess(mapGetLeadContactsResponse(payload.data.lead_contacts)),
          }
        }

        return state
      })

    /**
     * Get Lead Activities
     */
    builder
      .addCase(getLeadActivities.pending, (state, { meta }: PayloadAction) => ({
        ...state,
        leadActivities: setLoading(meta.requestId),
      }))
      .addCase(getLeadActivities.fulfilled, (state, { meta, payload }) => {
        if (payload.status === FETCHING_STATUS.fail) {
          return {
            ...state,
            leadActivities: payload,
          }
        }

        return {
          ...state,
          leadActivities: setSuccess(
            { ...mapLeadActivities(payload.data), fetchPayload: meta.arg as GetLeadActivitiesPayload },
            meta.requestId,
          ),
        }
      })

    /**
     * Get Lead Tasks
     */
    builder
      .addCase(getLeadTasks.pending, (state, { meta }: PayloadAction) => ({
        ...state,
        leadTasks: {
          page: setLoading(meta.requestId),
          data: meta.arg.last_record_id && state.leadTasks.data ? [...state.leadTasks.data] : undefined,
        },
      }))
      .addCase(getLeadTasks.fulfilled, (state, { meta, payload }) => {
        if (payload.status === 'FAIL') {
          return {
            ...state,
            leadTasks: {
              page: payload,
              data: state.leadTasks.data ? [...state.leadTasks.data] : undefined,
            },
          }
        }
        const leadTasks = mapLeadTasks(payload.data)
        return {
          ...state,
          leadTasks: {
            page: setSuccess({ totalCount: leadTasks.totalCount, fetchPayload: meta.arg }, meta.requestId),
            data: state.leadTasks.data ? [...state.leadTasks.data, ...leadTasks.data] : [...leadTasks.data],
          },
        }
      })

    builder.addCase(createCallLog.fulfilled, (state, { meta, payload }) => {
      if (payload.status === FETCHING_STATUS.success && payload.data.success) {
        meta.arg.onSuccess(payload.data)

        const {
          data: { task },
        } = payload.data

        // Add callback task in tasks list
        if (state.leadTasks.page.status === FETCHING_STATUS.success && state.leadTasks.data && task) {
          const currentStoreData = [...state.leadTasks.data].filter((i) => {
            if (i.leadTaskId != mapLeadTask(task)['leadTaskId']) {
              return i
            }
          })
          return {
            ...state,
            leadTasks: {
              ...state.leadTasks,
              data: [mapLeadTask(task), ...currentStoreData],
            },
          }
        }
        return
      }

      if (payload.status === FETCHING_STATUS.fail) {
        meta.arg.onFail(payload.error)
        return
      }

      meta.arg.onFail()
    })

    builder.addCase(updateCallLog.fulfilled, (_, { meta, payload }) => {
      if (payload.status === FETCHING_STATUS.success && payload.data.success) {
        meta.arg.onSuccess(payload.data)
        return
      }

      meta.arg.onFail()
    })

    builder
      .addCase(updateLostReason.pending, (state) => ({
        ...state,
        leadInvalidReasons: setLoading(),
        leadLostReasons: setLoading(),
      }))
      .addCase(updateLostReason.fulfilled, (state, { payload }) => {
        if (payload.status === FETCHING_STATUS.fail) {
          return {
            ...state,
            leadInvalidReasons: payload,
            leadLostReasons: payload,
          }
        }

        return {
          ...state,
          leadInvalidReasons: initialState.leadInvalidReasons,
          leadLostReasons: initialState.leadLostReasons,
        }
      })

    builder.addCase(updateLiveCallLog.fulfilled, (_, { meta, payload }) => {
      if (payload.status === FETCHING_STATUS.success && payload.data.success) {
        meta.arg.onSuccess(payload.data)
        return
      }

      if (payload.status === FETCHING_STATUS.fail) {
        meta.arg.onFail(payload.error)
        return
      }

      meta.arg.onFail()
    })

    /**
     * Get Lead Documents
     */
    builder
      .addCase(getLeadDocuments.pending, (state, { meta }: PayloadAction) => ({
        ...state,
        leadDocuments: setLoading(meta.requestId),
      }))
      .addCase(getLeadDocuments.fulfilled, (state, { meta, payload }) => {
        if (payload.status === 'FAIL') {
          return {
            ...state,
            leadDocuments: payload,
          }
        }
        const leadDocuments = mapLeadDocuments(payload.data)
        return {
          ...state,
          leadDocuments: setSuccess({ ...leadDocuments, fetchPayload: meta.arg }, meta.requestId),
        }
      })

    builder
      .addCase(downloadDocument.pending, (state) => ({
        ...state,
        leadDownloadDocument: setLoading(),
      }))
      .addCase(downloadDocument.fulfilled, (state, { meta, payload }) => {
        switch (payload.status) {
          case FETCHING_STATUS.success:
            const { document_url, file_name, file_size, mime_type, thumbnail_file_name } = payload.data

            meta.arg.onSuccess({
              documentUrl: document_url,
              fileName: file_name,
              fileSize: file_size,
              mimeType: mime_type,
              thumbnailFileName: thumbnail_file_name,
            })
            break
          default:
            meta.arg.onFail()
        }

        return { ...state, leadDownloadDocument: unSet() }
      })

    /**
     * Lead Notes
     */
    builder.addCase(createLeadNote.fulfilled, (_, { meta, payload }) => {
      if (payload.status === FETCHING_STATUS.success && payload.data.success) {
        meta.arg.onSuccess()
        return
      }

      meta.arg.onFail()
    })

    builder.addCase(deleteLeadNote.fulfilled, (_, { meta, payload }) => {
      if (payload.status === FETCHING_STATUS.success && payload.data.success) {
        meta.arg.onSuccess()
        return
      }

      meta.arg.onFail()
    })

    builder.addCase(updateLeadNote.fulfilled, (_, { meta, payload }) => {
      if (payload.status !== FETCHING_STATUS.success || !payload.data.success) {
        meta.arg.onFail()
        return
      }

      meta.arg.onSuccess()
    })

    builder
      .addCase(uploadDocument.pending, (state, { meta }) => {
        const { lead_id, file_name, file_size } = meta.arg
        const document = {
          uploadRequestId: meta.requestId,
          fileName: file_name,
          fileSize: file_size,
          status: 'LOADING',
        }

        const prevDocuments = state.leadDetailWindows.upload ? state.leadDetailWindows.upload.data.documents : []
        return {
          ...state,
          leadDetailWindows: {
            ...state.leadDetailWindows,
            upload: {
              ...state.leadDetailWindows.upload,
              data: {
                documents: [...prevDocuments, document],
                leadId: lead_id,
              },
            },
          },
        }
      })
      .addCase(uploadDocument.fulfilled, (state, { meta, payload }) => {
        const prevDocuments = state.leadDetailWindows.upload ? [...state.leadDetailWindows.upload.data.documents] : []
        const docIndex = prevDocuments.findIndex((i: UploadWindowItem) => i.uploadRequestId === meta.requestId)
        if (docIndex < 0) {
          return {
            ...state,
          }
        }

        const doc = {
          ...prevDocuments[docIndex],
        }

        if (payload.status === 'FAIL') {
          doc.status = 'FAIL'
          doc.error = 'Failed to upload file'
        } else {
          doc.status = 'SUCCESS'
          doc.documentId = payload.data.document_id
        }

        prevDocuments[docIndex] = doc

        return {
          ...state,
          leadDetailWindows: {
            ...state.leadDetailWindows,
            upload: {
              ...state.leadDetailWindows.upload,
              data: {
                documents: [...prevDocuments],
                leadId: meta.arg.lead_id,
              },
            },
          },
        }
      })

    builder.addCase(getPendingDocuments.fulfilled, (state, { meta, payload }) => {
      if (payload.status === 'FAIL') {
        return {
          ...state,
        }
      }

      const prevDocuments = state.leadDetailWindows.upload
        ? [...state.leadDetailWindows.upload.data.documents].filter(
            (i) => i.status === 'FAIL' || i.status === 'LOADING',
          )
        : []
      const nextDocuments = mapPendingDocuments(payload.data)

      if (!nextDocuments.length) {
        return {
          ...state,
        }
      }

      const uploadWindowProps = (() => {
        if (!state.leadDetailWindows.upload) {
          return {
            isMinimized: false,
            isPadded: false,
            canClose: false,
            title: 'upload',
            type: 'upload',
            windowId: uuidv4(),
            windowState: 'active',
            icon: ['far', 'cloud-upload'],
            minimizeIcon: 'chevron-up',
            restoreIcon: 'chevron-down',
            width: 460,
          }
        }
        return {}
      })()

      return {
        ...state,
        leadDetailWindows: {
          ...state.leadDetailWindows,
          upload: {
            ...state.leadDetailWindows.upload,
            ...uploadWindowProps,
            data: {
              documents: [...prevDocuments, ...nextDocuments],
              leadId: meta.arg.lead_id,
            },
          },
        },
      }
    })

    builder
      .addCase(getLeadInvalidReasons.pending, (state) => ({
        ...state,
        leadInvalidReasons: setLoading(),
      }))
      .addCase(getLeadInvalidReasons.fulfilled, (state, { meta, payload }) => {
        if (payload.status === 'FAIL') {
          return {
            ...state,
            leadInvalidReasons: payload,
          }
        }

        const mappedPayload = mapLeadInvalidLostReasons(payload.data)

        if (meta.arg.onSuccess) {
          meta.arg.onSuccess(mappedPayload)
        }

        return {
          ...state,
          leadInvalidReasons: setSuccess(mappedPayload),
        }
      })

    builder
      .addCase(getLeadLostReasons.pending, (state) => ({
        ...state,
        leadLostReasons: setLoading(),
      }))
      .addCase(getLeadLostReasons.fulfilled, (state, { payload }) => {
        if (payload.status === 'FAIL') {
          return {
            ...state,
            leadLostReasons: payload,
          }
        }
        return {
          ...state,
          leadLostReasons: setSuccess(mapLeadInvalidLostReasons(payload.data)),
        }
      })

    /**
     * Lead Appointment
     */
    builder
      .addCase(getLeadAppointment.pending, (state) => ({ ...state, leadAppointment: setLoading() }))
      .addCase(getLeadAppointment.fulfilled, (state, { payload }) => {
        if (payload.status === 'FAIL') {
          return { ...state, leadAppointment: payload }
        }

        const mappedData = mapLeadAppointment(payload.data)

        return {
          ...state,
          leadAppointment: setSuccess(mappedData),
          leadDetailWindows: {
            ...state.leadDetailWindows,
            appointment: { ...state.leadDetailWindows.appointment, data: mappedData },
          },
        }
      })

    builder.addCase(updateLeadAppointmentStatus.fulfilled, (_, { meta, payload }) => {
      if (payload.status === FETCHING_STATUS.success && payload.data.success) {
        meta.arg.onSuccess()
        return
      }

      meta.arg.onFail()
    })
  },
})

export default reducer
export { actions }

export const mapLeadAppointment: (data: GetLeadAppointmentResponse) => LeadAppointment = (data) => {
  const {
    address: { address_label, address_line_1, address_line_2, city, country, postal_code, province, suburb, lat, lng },
    assignees,
    create_ts,
    created_by,
    created_by_name,
    created_by_photo,
    duration,
    end_time,
    internal_note_email_status,
    is_active,
    is_all_day,
    is_coaching_visit,
    is_d2_delivery,
    is_confirmed,
    is_overdue,
    lead_activity_id,
    lead_activity_type,
    lead_appointment_id,
    lead_appointment_status,
    lead_appointment_status_id,
    lead_id,
    lead_meeting_minute_id,
    meeting_minute_email_skipped_by,
    meeting_minute_email_skipped_ts,
    meeting_minute_email_status,
    internal_note_id,
    start_time,
    title,
    update_ts,
    updated_by,
    updated_by_name,
    note,
    meeting_location,
    meeting_time,
    has_accepted_pdpa,
    clients,
    participants,
    meeting_type_id,
    meeting_type_name,
    meeting_location_type,
    reason_codes,
  } = data

  const formatted = formatAddress({
    addressLine1: address_line_1,
    addressLine2: address_line_2,
    city,
    country,
    formatted: address_label,
    postalCode: postal_code,
    province,
    suburb,
  })

  return {
    address: {
      addressLine1: address_line_1,
      addressLine2: address_line_2,
      city,
      country,
      formatted,
      postalCode: postal_code,
      province,
      suburb,
      lat,
      lng,
    },
    addressDisplay: formatted,
    assigneeIds: assignees.map((i) => i.assignee_id),
    assignees: assignees.map((i) => ({
      assignee: i.assignee,
      assigneeEmail: i.assignee_email,
      assigneePhoto: i.assignee_photo,
      assigneeId: i.assignee_id,
    })),
    createdBy: created_by,
    createdByName: created_by_name,
    createdByPhoto: created_by_photo,
    createTs: create_ts,
    duration,
    endTime: parseDate(end_time),
    internalNoteEmailStatus: internal_note_email_status,
    isActive: is_active,
    isAllDay: is_all_day,
    isCoachingVisit: is_coaching_visit,
    isD2Delivery: is_d2_delivery,
    isConfirmed: is_confirmed,
    isOverdue: is_overdue,
    leadActivityId: lead_activity_id,
    leadActivityType: lead_activity_type,
    leadAppointmentId: lead_appointment_id,
    leadAppointmentStatus: lead_appointment_status,
    leadAppointmentStatusId: lead_appointment_status_id,
    leadId: lead_id,
    leadMeetingMinutesId: lead_meeting_minute_id,
    meetingMinuteEmailSkippedBy: meeting_minute_email_skipped_by,
    meetingMinuteEmailSkippedTs: meeting_minute_email_skipped_ts,
    meetingMinuteEmailStatus: meeting_minute_email_status,
    meetingTypeId: meeting_type_id,
    meetingTypeName: meeting_type_name,
    internalNoteId: internal_note_id,
    startTime: parseDate(start_time),
    title,
    updatedBy: updated_by,
    updatedByName: updated_by_name,
    updateTs: update_ts,
    note,
    meetingLocation: meeting_location,
    meetingTime: meeting_time,
    hasAcceptedPDPA: has_accepted_pdpa,
    participants,
    clients:
      clients?.map((i) => ({
        contactPersonId: i.contact_person_id,
        name: i.name,
        contactInfoId: i.contact_info_id,
        contactValue: i.contact_value,
        isDeleted: i.is_deleted,
        isActive: i.is_active,
      })) ?? [],
    meetingLocationType: meeting_location_type,
    reasonCodes: reason_codes,
  }
}

const LAST_ACTIVITY_TYPE: { [key in LeadActivityType]: LeadActivityType } = {
  'Lead Lifecycle Event': 'Lead Lifecycle Event',
  Appointment: 'Meeting',
  Call: 'Call',
  Email: 'Email',
  'Email (Log)': 'Email (Log)',
  'Live Call': 'Live Call',
  Meeting: 'Meeting',
  Note: 'Note',
  'To Do': 'To Do',
  Message: 'Message',
}

export const mapGetMergeableLeadListResponse = (
  result: MergeableLeadResponse,
): { count: string; data: MergeableLeadData[] } => ({
  count: result.total || '0',
  data: result?.leads?.map((lead) => ({
    leadId: lead?.lead_id,
    leadInDate: lead?.lead_in_date,
    leadName: lead?.lead_name,
    leadOwnerGroupName: lead?.lead_owner_group_name,
    leadOwnerName: lead?.lead_owner_name,
    leadStatusName: lead?.lead_status_name,
    leadTypeName: lead?.lead_type_name,
    duplicateNote: lead?.duplicate_note,
    lastActivityTs: lead?.last_activity_ts,
    lastActivityType: lead?.last_activity_type,
  })),
})

export const mapGetLeadResponse = (result: GetLeadsResponse): { count: string; data: LeadInfo[] } => ({
  count: result.num_results,
  data: result.leads.map((lead) => ({
    lastActivity: lead.last_activity?.last_activity_type
      ? {
          lastActivityStatus: lead.last_activity?.last_activity_status,
          lastActivityTs: lead.last_activity?.last_activity_ts,
          lastActivityType: LAST_ACTIVITY_TYPE[lead.last_activity.last_activity_type],
        }
      : null,
    lastActivityOutcome: lead.last_activity?.last_activity_outcome,
    leadId: lead.lead_id,
    leadInDate: lead.lead_in_date,
    leadAddress: {
      ...lead.address,
      addressLine: lead.address?.address_line,
    },
    leadLocation: lead.address?.city || lead.address?.province || lead.address?.country,
    leadName: lead.lead_name,
    leadOwner: lead.lead_owner_name,
    leadOwnerId: lead.lead_owner_id,
    leadOwnerTeam: lead.lead_owner_team_name,
    leadOwnerTeamId: lead.lead_owner_team_id,
    collaborators: {
      additionalOwner: lead.additional_owner_name,
      otherAdditionalOwnerNames: lead.additional_owner_names,
      otherAdditionalOwnerCount: lead.other_additional_owner_count,
    },
    leadType: lead.lead_type_name as CustomerType,
    leadSource: lead.lead_source_name,
    leadSourceTypeName: lead.lead_source_type_name,
    leadCampaign: lead.lead_campaign_name,
    status: lead.lead_status_name as Status,
    priority: lead.lead_priority_name?.toLocaleLowerCase() as Priority,
    registrationNumber: lead.company_reg_no,
    industry: lead.industry_name,
    convertedDate: lead.converted_ts,
    convertedBy: lead.converted_by,
    closedDate: lead.closed_ts,
    closedReason: lead.close_reason,
    closedReasonDetails: lead.closed_reason_details,
    closedBy: lead.closed_by_name,
    recycledStatus: lead.is_recycle,
    recycledDate: lead.last_recycle_ts,
    customFields: lead.custom_form_fields
      ? lead.custom_form_fields.map((item) => ({ [item.accessor]: item.value }))
      : [],
    hasOpenOpportunity: lead.has_open_opportunity,
    nextActivity: lead.next_activity?.next_activity_type
      ? {
          nextActivityTs: lead.next_activity?.next_activity_ts,
          nextActivityType: LAST_ACTIVITY_TYPE[lead.next_activity.next_activity_type],
        }
      : null,
    totalMarketPotential: lead?.market_potential,
    deletedDate: lead.delete_ts,
    deletedReason: lead.deleted_reason,
    deletedBy: lead.deleted_by_name,
    isDeleted: lead.is_deleted,
    totalCalls: lead.total_calls,
  })),
})

export const mapSearchLeadResponse = (result: GetLeadSearchResponse): { count: string; data: LeadInfoSearch[] } => ({
  count: result.num_results,
  data: result.leads.map((lead) => ({
    lastActivity: lead.last_activity_ts
      ? {
          lastActivityTs: lead.last_activity_ts,
        }
      : null,
    leadId: lead.lead_id,
    leadName: lead.lead_name,
    leadOwner: lead.lead_owner_name,
    leadOwnerTeam: lead.lead_owner_team_name,
    leadStatusGroupName: lead?.lead_status_group_name,
  })),
})

const mapLeadInvalidLostReasons: (result: LeadInvalidLostResponse) => Array<LeadInvalidLostReason> = (result) =>
  result.map(({ id, close_reason }) => ({ id, closeReason: close_reason }))

export const mapLeadAddress: (result: LeadAddressResponse) => LeadDetails.Address[] = (result) =>
  result?.map((address) => ({
    addressId: address.address_id,
    addressLine1: address.address_line_1,
    addressLine2: address.address_line_2 || '',
    ward: address.ward || '',
    city: address.city || '',
    postalCode: address.postal_code || '',
    suburb: address.suburb || '',
    province: address.province || '',
    country: address.country,
    lat: address.latitude,
    lng: address.longitude,
    id: 0, // value only used in FE and not served by endpoint
    isActive: address.is_active,
    isPrimary: address.is_primary,
    formatted: formatAddress({
      addressLine1: address.address_line_1,
      addressLine2: address.address_line_2 ?? '',
      city: address.city ?? '',
      country: address.country,
      formatted: address.address_label,
      postalCode: address.postal_code ?? '',
      province: address.province ?? '',
      suburb: address.suburb ?? '',
      ward: address.ward ?? '',
    }),
  })) || []

export const mapLeadDetailResponse: (result: LeadDetailResponse) => LeadDetail = (result) => ({
  closedTs: result.closed_ts,
  closedBy: result.closed_by_name,
  convertedBy: result.converted_by,
  convertedTs: result.converted_ts,
  deletedBy: result.deleted_by,
  deletedByUser: result.deleted_by_user,
  deletedTs: result.deleted_ts,
  description: result.description,
  dispatch: result.dispatch
    ? {
        leadAppointmentId: result?.dispatch?.lead_appointment_id,
        leadTaskId: result?.dispatch?.lead_task_id,
        userId: result.dispatch.user_id,
      }
    : undefined,
  isDeleted: result.is_deleted,
  isRecycled: result.is_recycle,
  lastActivity: {
    lastActivityStatus: result.last_activity?.last_activity_status,
    lastActivityType: result.last_activity?.last_activity_type,
    lastActivityTs: result.last_activity?.last_activity_ts,
  },
  lastRecycledBy: result.last_recycle_by,
  lastRecycledTs: result.last_recycle_ts,
  leadCloseReason: result.lead_close_reason,
  leadCloseReasonId: result.lead_close_reason_id,
  leadClosedReasonDetails: result.lead_closed_reason_details,
  leadId: result.lead_id,
  leadInDate: new Date(result.lead_in_date).toString(),
  leadName: result.lead_name,
  leadTypeId: result.lead_type_id,
  leadTypeName: result.lead_type_name,
  leadPriorityId: result.lead_priority_id,
  leadPriorityName: result.lead_priority_name,
  leadStatusId: result.lead_status_id,
  leadStatusName: result.lead_status_name,
  leadSourceId: result.lead_source_id,
  leadSourceName: result.lead_source_name,
  leadSourceStatus: result.lead_source_status,
  leadCampaignId: result.lead_campaign_id,
  leadCampaignName: result.lead_campaign_name,
  leadCampaignStatus: result.lead_campaign_status,
  leadProcessColor: result.lead_process_color,
  leadProcessId: result.lead_process_id,
  leadProcessName: result.lead_process_name,
  leadProcessStage: result.lead_process_stage,
  metadata: result.metadata,
  owner: result.owner,
  ownerTeam: result.owner_team,
  quotationId: result.quotation_id,
  additionalOwners: result.additional_owners,
  company: {
    id: result.company.id,
    isTaxable: result.company.is_taxable,
    website: result.company.website,
    name: result.company.name,
    industryId: result.company.industry_id,
    registrationNumber: result.company.registration_number,
    numEmployees: result.company.num_employees || 0,
    annualRevenue: result.company.annual_revenue || 0,
  },
  customer: {
    convertedBy: result.customer.converted_by,
    convertedByName: result.customer.converted_by_name,
    createdTs: result.customer.created_ts,
    customerId: result.customer.customer_id,
    customerStatusId: result.customer.customer_status_id,
  },
  camsUsername: result.cams_client_username,
  customFields: result.custom_fields ? mapCustomFieldLeadDetail(result.custom_fields) : [],
  marketPotential: result.market_potential,
  dispositionId: result.disposition_id,
  dispositionName: result.disposition_name,
  viewType: result.view_type,
  opportunityStatusCount: result.opportunity_status_count,
  nextActivity: {
    nextActivityType: result.next_activity?.next_activity_type,
    nextActivityTs: result.next_activity?.next_activity_ts,
  },
  canAssignLeadToOthers: result.can_assign_lead_to_others,
  canAssignLeadToSelf: result.can_assign_lead_to_self,
  lastAssignedTeamId: result.last_assigned_team_id,
  lastAssignedUserId: result.last_assigned_user_id,
  isLeadMerged: result.is_lead_merged,
})

export const mapLeadActivities: (result: LeadActivitiesResponse) => LeadActivities = (result) => ({
  totalCount: result.total_count,
  data: result.data.map((activity) => ({
    createTs: activity.create_ts,
    deleteTs: activity.delete_ts,
    leadActivityId: activity.lead_activity_id,
    leadActivityTypeId: activity.lead_activity_type_id,
    leadActivityType: activity.lead_activity_type,
    leadLifeCycleEvent: activity.lead_life_cycle_event
      ? {
          createTs: activity.lead_life_cycle_event.create_ts,
          createdBy: activity.lead_life_cycle_event.created_by,
          createdByName: activity.lead_life_cycle_event.created_by_name,
          createdByPhoto: activity.lead_life_cycle_event.created_by_photo,
          subType: activity.lead_life_cycle_event.data.sub_type,
          dispositionId: activity.lead_life_cycle_event.data.disposition_id,
          closeReason: activity.lead_life_cycle_event.data.close_reason,
          closedReasonDetails: activity.lead_life_cycle_event.data.closed_reason_details,
          assigneeTeam: activity.lead_life_cycle_event.data.assignee_team,
          assigneeUser: activity.lead_life_cycle_event.data.assignee_user,
          collaborators: activity.lead_life_cycle_event.data.collaborators,
        }
      : null,
    leadAppointment: activity.lead_appointment
      ? {
          address: {
            city: activity.lead_appointment.address.city,
            suburb: activity.lead_appointment.address.suburb,
            country: activity.lead_appointment.address.country,
            province: activity.lead_appointment.address.province,
            postalCode: activity.lead_appointment.address.postal_code,
            addressLine1: activity.lead_appointment.address.address_line_1,
            addressLine2: activity.lead_appointment.address.address_line_2,
            formatted: activity.lead_appointment.address.address_label,
          },
          assigneeId: activity.lead_appointment.assignees?.map((i) => ({
            assigneeId: i.assignee_id,
            assignee: i.assignee,
            assigneePhoto: i.assignee_photo,
            assigneeEmail: i.assignee_email,
          })),
          assignees: activity.lead_appointment.assignees?.map((i) => ({
            assigneeId: i.assignee_id,
            assignee: i.assignee,
            assigneePhoto: i.assignee_photo,
            assigneeEmail: i.assignee_email,
          })),
          assignee: activity.lead_appointment.assignee,
          assigneePhoto: activity.lead_appointment.assignee_photo,
          createTs: activity.lead_appointment.create_ts,
          createdBy: activity.lead_appointment.created_by,
          createdByName: activity.lead_appointment.created_by_name,
          createdByPhoto: activity.lead_appointment.created_by_photo,
          duration: activity.lead_appointment.duration,
          endTime: parseDate(activity.lead_appointment.end_time),
          internalNoteId: activity.lead_appointment.internal_note_id,
          internalNoteEmailStatus: null,
          isActive: activity.lead_appointment.is_active,
          isAllDay: activity.lead_appointment.is_all_day,
          isCoachingVisit: activity.lead_appointment.is_coaching_visit,
          isD2Delivery: activity.lead_appointment.is_d2_delivery,
          isConfirmed: activity.lead_appointment.is_confirmed,
          isOverdue: activity.lead_appointment.is_overdue,
          leadActivityId: activity.lead_activity_id,
          leadActivityType: activity.lead_activity_type,
          leadAppointmentId: activity.lead_appointment.lead_appointment_id,
          leadAppointmentStatus: activity.lead_appointment.lead_appointment_status,
          leadAppointmentStatusId: activity.lead_appointment.lead_appointment_status_id,
          leadId: activity.lead_appointment.lead_id,
          leadMeetingMinutesId: activity.lead_appointment.lead_meeting_minute_id,
          meetingMinuteEmailSkippedBy: activity.lead_appointment.meeting_minute_email_skipped_by,
          meetingMinuteEmailSkippedTs: activity.lead_appointment.meeting_minute_email_skipped_ts,
          meetingMinuteEmailStatus: LEAD_MEETING_MINUTE_EMAIL_STATUS.pending,
          meetingTypeId: activity.lead_appointment.meeting_type_id,
          meetingTypeName: activity.lead_appointment.meeting_type_name,
          updateTs: activity.lead_appointment.update_ts,
          updatedBy: activity.lead_appointment.updated_by,
          updatedByName: activity.lead_appointment.updated_by_name,
          startTime: activity.lead_appointment.start_time,
          title: activity.lead_appointment.title,
          note: activity.lead_appointment.note,
          meetingLocation: activity.lead_appointment.meeting_location,
          meetingLocationType: activity.lead_appointment.meeting_location_type ?? {
            id: 1,
            name: 'On Site',
            accessor: 'on_site',
            is_default: true,
          },
          meetingTime: activity.lead_appointment.meeting_time,
          hasAcceptedPDPA: activity.lead_appointment.has_accepted_pdpa,
          participants: activity.lead_appointment.participants,
          clients:
            activity.lead_appointment.clients?.map((i) => ({
              contactPersonId: i.contact_person_id,
              name: i.name,
              contactInfoId: i.contact_person_id,
              contactValue: i.contact_value,
              isDeleted: i.is_deleted,
              isActive: i.is_active,
            })) ?? [],
          reasonCodes: activity.lead_appointment.reason_codes,
        }
      : null,
    leadNote: activity.lead_note
      ? {
          createTs: activity.lead_note.create_ts,
          createdBy: activity.lead_note.created_by,
          createdByName: activity.lead_note.created_by_name,
          createdByPhoto: activity.lead_note.created_by_photo,
          leadNoteId: activity.lead_note.lead_note_id,
          note: activity.lead_note.note,
          updateTs: activity.lead_note.update_ts,
          updatedBy: activity.lead_note.updated_by,
          updatedByName: activity.lead_note.updated_by_name,
        }
      : null,
    leadCallLog: activity.lead_call_log
      ? {
          createTs: activity.lead_call_log.create_ts,
          createdBy: activity.lead_call_log.created_by,
          createdByName: activity.lead_call_log.created_by_name,
          createdByPhoto: activity.lead_call_log.created_by_photo,
          leadActivityId: activity.lead_call_log.lead_activity_id,
          callLogId: activity.lead_call_log.call_log_id,
          callback: activity.lead_call_log.callback,
          contactInfo: {
            contactInfoId: activity.lead_call_log.contact_info.contact_info_id,
            fname: activity.lead_call_log.contact_info.fname,
            lname: activity.lead_call_log.contact_info.lname,
            contactInfoTypeId: activity.lead_call_log.contact_info.contact_info_type_id,
            contactValue: activity.lead_call_log.contact_info.contact_value,
            isOtherNumber: activity.lead_call_log.contact_info.is_other_number,
            isPrimaryContact: activity.lead_call_log.contact_info.is_primary_contact,
            otherContactInfoTypeId: activity.lead_call_log.contact_info.other_contact_info_type_id,
            otherContactValue: activity.lead_call_log.contact_info.other_contact_value,
          },
          callTime: activity.lead_call_log.call_time,
          callStatus: activity.lead_call_log.call_status,
          callStatusId: activity.lead_call_log.call_status_id,
          callStatusOutcome: activity.lead_call_log.call_status_outcome,
          callStatusOutcomeId: activity.lead_call_log.call_status_outcome_id,
          callStatusOutcomeActionId: activity.lead_call_log.call_status_outcome_action_id,
          callStatusOutcomeActionCode: activity.lead_call_log.call_status_outcome_action_code,
          callNote: activity.lead_call_log.call_note,
          isFirstCall: activity.lead_call_log.is_first_call,
          isCallbackScheduled: activity.lead_call_log.is_callback_scheduled,
          isContract: activity.lead_call_log.is_contract,
          isCoachingVisit: activity.lead_call_log.is_coaching_visit,
          isD2Delivery: activity.lead_call_log.is_d2_delivery,
          isMessage: activity.lead_call_log.is_message,
          additionalInfo: activity.lead_call_log.additional_info,
          attachments: activity.lead_call_log.attachments,
        }
      : null,
    liveCall: activity.live_call
      ? ({
          createTs: activity.live_call.create_ts,
          createdBy: activity.live_call.created_by,
          createdByName: activity.live_call.created_by_name,
          createdByPhoto: activity.live_call.created_by_photo,
          callback: activity.live_call.callback,
          contactInfo: {
            contactInfoId: activity.live_call.contact_info.contact_info_id,
            fname: activity.live_call.contact_info.fname,
            lname: activity.live_call.contact_info.lname,
            contactInfoTypeId: activity.live_call.contact_info.contact_info_type_id,
            contactValue: activity.live_call.contact_info.contact_value,
            isOtherNumber: activity.live_call.contact_info.is_other_number,
            isPrimaryContact: activity.live_call.contact_info.is_primary_contact,
            otherContactInfoTypeId: activity.live_call.contact_info.other_contact_info_type_id,
            otherContactValue: activity.live_call.contact_info.other_contact_value,
          },
          callStatus: activity.live_call.call_status,
          callStatusId: activity.live_call.call_status_id,
          callStatusOutcome: activity.live_call.call_status_outcome,
          callStatusOutcomeId: activity.live_call.call_status_outcome_id,
          callNote: activity.live_call.call_note,
          isFirstCall: activity.live_call.is_first_call,
          isCallbackScheduled: activity.live_call.is_callback_scheduled,
          isContract: activity.live_call.is_contract,
          liveCallId: activity.live_call.live_call_id,
          callLogId: activity.live_call.call_log_id,
          leadActivityId: activity.live_call.lead_activity_id,
          duration: activity.live_call.duration,
          liveCallStatus: activity.live_call.live_call_status,
          recordingPath: activity.live_call.recording_path,
          updateTs: activity.live_call.update_ts,
          startTime: activity.live_call.start_time,
          endTime: activity.live_call.end_time,
          additionalInfo: activity.live_call.additional_info,
          connectionDuration: activity.live_call.connection_duration,
          totalDuration: activity.live_call.total_duration,
          ringDuration: activity.live_call.ring_duration,
          talkTimeDuration: activity.live_call.talktime_duration,
        } as LiveCallLog)
      : null,
    leadEmail: activity.lead_email
      ? {
          leadEmailId: activity.lead_email.lead_email_id,
          emailOrigin: activity.lead_email.email_origin,
          emailType: activity.lead_email.email_type,
          emailStatus: activity.lead_email.email_status,
          subject: activity.lead_email.subject,
          body: activity.lead_email.body,
          recipients: activity.lead_email.recipients,
          attachments: activity.lead_email.attachments,
          tasks: activity.lead_email.tasks,
          sentTs: activity.lead_email.sent_ts,
          createTs: activity.lead_email.create_ts,
          createdBy: activity.lead_email.created_by,
          createdByName: activity.lead_email.created_by_name,
          updateTs: activity.lead_email.update_ts,
          updatedBy: activity.lead_email.updated_by,
          updatedByName: activity.lead_email.updated_by_name,
          createdByPhoto: activity.lead_email.created_by_photo,
        }
      : null,
    recordId: activity.record_id,
    updateTs: activity.update_ts,
  })),
})

const mapLeadTasks: (result: LeadTasksResponse) => LeadTasks = (result) => ({
  totalCount: result.total_count,
  data: result.lead_tasks?.map((task) => mapLeadTask(task)) || [],
})

export const mapLeadTask: (data: SingleLeadTask) => LeadTask = (data) => ({
  leadTaskId: data.lead_task_id,
  leadId: data.lead_id,
  recordId: parseInt(data.record_id),
  name: data.name,
  assigneeId: data.assignee_id,
  assignee: data.assignee,
  assigneePhoto: data.assignee_photo,
  assigneeTeamId: data.assignee_team_id,
  canAssignTeam: data.is_first_call || data.is_callback,
  dueDate: data.due_date,
  dueDateTime: data.due_date_time,
  resolveTs: data.resolve_ts,
  status: data.status,
  createdBy: data.created_by,
  creator: data.creator,
  isCallback: data.is_callback,
  isFirstCall: data.is_first_call,
  isOverdue: data.is_overdue,
  createTs: data.create_ts,
  updateTs: data.update_ts,
  isCancelled: data.is_cancelled,
})

const LeadCallLogEmptyValues: LeadCallLogActivity = {
  createTs: '',
  createdBy: '',
  createdByName: '',
  createdByPhoto: '',
  leadActivityId: '',
  callLogId: '',
  callback: {
    assigneeId: '',
    assigneeTeamId: '',
    dueDate: '',
    dueDateTime: '',
    name: '',
    isCoachingVisit: false,
    isD2Delivery: false,
    isManualCallbackAssignee: false,
    isManualCallbackDuedatetime: false,
  },
  contactInfo: {
    contactInfoId: '',
    fname: '',
    lname: '',
    contactInfoTypeId: '',
    contactValue: '',
    isOtherNumber: false,
    isPrimaryContact: false,
    otherContactInfoTypeId: '',
    otherContactValue: '',
  },
  callTime: '',
  callStatus: undefined,
  callStatusId: undefined,
  callStatusOutcome: '',
  callStatusOutcomeId: '',
  callStatusOutcomeActionId: '',
  callStatusOutcomeActionCode: '',
  callNote: '',
  isFirstCall: false,
  isCallbackScheduled: false,
  isContract: false,
  isCoachingVisit: false,
  isD2Delivery: false,
  attachments: [],
  isMessage: false,
  additionalInfo: {},
}

const LiveCallLogEmptyValues: LiveCallLog = {
  createTs: '',
  createdBy: '',
  createdByName: '',
  createdByPhoto: '',
  liveCallId: '',
  callback: {
    assigneeId: '',
    assigneeTeamId: '',
    dueDate: '',
    dueDateTime: '',
    name: '',
    isCoachingVisit: false,
    isD2Delivery: false,
    isManualCallbackAssignee: false,
    isManualCallbackDuedatetime: false,
  },
  contactInfo: {
    contactInfoId: '',
    fname: '',
    lname: '',
    contactInfoTypeId: '',
    contactValue: '',
    isOtherNumber: false,
    isPrimaryContact: false,
    otherContactInfoTypeId: '',
    otherContactValue: '',
  },
  callStatus: undefined,
  callStatusId: undefined,
  isUpdatedCallStatusId: false,
  callStatusOutcome: '',
  callStatusOutcomeId: '',
  callNote: '',
  isFirstCall: false,
  isCallbackScheduled: false,
  duration: '',
  liveCallStatus: '',
  leadActivityId: '',
  recordingPath: null,
  updateTs: '',
  startTime: '',
  endTime: '',
  totalDuration: '',
  connectionDuration: '',
  ringDuration: '',
  talkTimeDuration: '',
}

const LeadNoteEmptyValues = {
  createTs: '',
  createdBy: '',
  createdByName: '',
  createdByPhoto: '',
  leadId: '',
  leadNoteId: '',
  note: '',
  updateTs: '',
  updatedBy: '',
  updatedByName: '',
}

type WindowInfo = {
  [key in LeadDetailWindowType]: {
    emptyValue?: LeadCallLogActivity | LeadNote | LiveCallLog
    referenceIdField:
      | 'callLogId'
      | 'leadAppointmentId'
      | 'leadCallId'
      | 'leadNoteId'
      | 'liveCallId'
      | 'leadId'
      | 'meetingMinuteId'
      | 'quotation_id'
      | 'leadTaskId'
    relatedStore:
      | 'leadAppointment'
      | 'leadCallLog'
      | 'leadNote'
      | 'liveCallLog'
      | 'uploadDocuments'
      | 'emailComposer'
      | 'quotation'
      | 'message'
      | 'leadCallTask'
  }
}

const WINDOW_CONFIG: {
  [key in LeadDetailWindowType]: {
    icon: string | string[]
    readOnlyIcon?: string | string[]
    width: number
    title: { create: string; update: string }
  }
} = {
  appointment: {
    icon: ['fal', 'pencil'],
    readOnlyIcon: ['fal', 'calendar-days'],
    title: {
      create: 'new_meeting_lbl',
      update: 'meeting_lbl',
    },
    width: 650,
  },
  call: {
    icon: ['far', 'phone'],
    title: {
      create: 'new_call_log_lbl',
      update: 'call_log_lbl',
    },
    width: 666,
  },
  task: {
    icon: ['far', 'pencil'],
    readOnlyIcon: ['far', 'circle-check'],
    title: {
      create: 'new_task_lbl',
      update: 'view_todo_task_lbl',
    },
    width: 666,
  },
  call_task: {
    icon: ['fal', 'pencil'],
    readOnlyIcon: ['far', 'circle-check'],
    title: {
      create: 'call_task_lbl',
      update: 'view_call_task_lbl',
    },
    width: 666,
  },
  message: {
    icon: ['far', 'messages'],
    title: {
      create: 'message_log_lbl',
      update: 'message_log_lbl',
    },
    width: 666,
  },
  email: {
    icon: ['fal', 'pencil'],
    title: {
      create: 'new_email',
      update: 'email_lbl',
    },
    width: 800,
  },
  liveCall: {
    icon: ['fas', 'headset'],
    title: {
      create: 'live_call_new',
      update: 'live_call',
    },
    width: 666,
  },
  note: {
    icon: ['fal', 'pencil'],
    title: {
      create: 'new_note_btn',
      update: 'note_lbl',
    },
    width: 400,
  },
  quotation: {
    icon: ['fal', 'pencil'],
    title: {
      create: 'quotation_new',
      update: 'quotation_lbl',
    },
    width: 1000,
  },
  upload: {
    icon: ['far', 'cloud-upload'],
    title: {
      create: 'new_call_log_lbl',
      update: 'call_log_lbl',
    },
    width: 460,
  },
  quotationEmail: {
    icon: ['fal', 'pencil'],
    title: {
      create: 'new_email',
      update: 'email_lbl',
    },
    width: 800,
  },
}

const LeadDetailWindowInfo: WindowInfo = {
  appointment: {
    referenceIdField: 'leadAppointmentId',
    relatedStore: 'leadAppointment',
  },
  call: {
    referenceIdField: 'callLogId',
    emptyValue: LeadCallLogEmptyValues,
    relatedStore: 'leadCallLog',
  },
  task: {
    referenceIdField: 'callLogId',
    emptyValue: LeadCallLogEmptyValues,
    relatedStore: 'leadCallLog',
  },
  call_task: {
    referenceIdField: 'leadTaskId',
    emptyValue: LeadCallLogEmptyValues,
    relatedStore: 'leadCallTask',
  },
  message: {
    referenceIdField: 'callLogId',
    emptyValue: LeadCallLogEmptyValues,
    relatedStore: 'message',
  },
  note: {
    emptyValue: LeadNoteEmptyValues,
    referenceIdField: 'leadNoteId',
    relatedStore: 'leadNote',
  },
  liveCall: {
    referenceIdField: 'liveCallId',
    emptyValue: LiveCallLogEmptyValues,
    relatedStore: 'liveCallLog',
  },
  upload: {
    referenceIdField: 'leadId',
    emptyValue: undefined,
    relatedStore: 'uploadDocuments',
  },
  email: {
    referenceIdField: 'meetingMinuteId',
    emptyValue: undefined,
    relatedStore: 'emailComposer',
  },
  quotation: {
    referenceIdField: 'quotation_id',
    emptyValue: undefined,
    relatedStore: 'quotation',
  },
  quotationEmail: {
    referenceIdField: 'quotation_id',
    emptyValue: undefined,
    relatedStore: 'emailComposer',
  },
}

export const LeadTaskInitialValue: LeadTask = {
  leadTaskId: '',
  leadId: '',
  recordId: 0,
  name: '',
  assigneeId: '',
  assignee: '',
  assigneePhoto: '',
  assigneeTeamId: null,
  dueDate: '',
  resolveTs: '',
  status: 'OPEN',
  createdBy: '',
  creator: '',
  isCallback: false,
  isFirstCall: false,
  isOverdue: false,
  createTs: '',
  updateTs: '',
  isCancelled: false,
}

const minimizeOtherLeadDetailWindows: (
  leadDetailWindows: LeadDetailWindows,
  restoredWindowType: LeadDetailWindowType,
) => LeadDetailWindows = (leadDetailWindows, restoredWindowType) => {
  const otherWindows = { ...leadDetailWindows }
  delete otherWindows[restoredWindowType]

  // Minimize other windows, if there are any
  if (Object.keys(otherWindows).length > 0) {
    Object.keys(otherWindows).forEach((k) => {
      const key = k as LeadDetailWindowType

      if (otherWindows[key]) {
        otherWindows[key] = {
          ...otherWindows[key],
          isMinimized: true,
        }
      }
    })
  }

  return otherWindows
}

const mapLeadDocuments: (result: GetLeadDocumentsResponse) => LeadDocuments = (result) => ({
  totalCount: result.total_count,
  data: result.data.map((document) => ({
    recordId: parseInt(document.record_id),
    documentId: document.document_id,
    fileName: document.file_name,
    fileSize: document.file_size,
    mimeType: document.mime_type,
    uploadedBy: document.uploaded_by,
    uploadedTs: document.uploaded_ts,
    documentUrl: document.document_url,
    thumbnailUrl: document.thumbnail_url,
    uploader: document.uploader,
  })),
})

const mapPendingDocuments: (result: GetPendingDocumentsResponse) => UploadWindowItem[] = (result) =>
  result.data.map((document) => ({
    documentId: document.document_id,
    fileName: document.file_name,
    status: 'SUCCESS',
  }))

export const mapGetLeadContactsResponse: (data: GetLeadContactsResponse[]) => LeadContacts = (result) =>
  result.map((leadContact: GetLeadContactsResponse) => ({
    id: leadContact.contact_id,
    contactTypeId: leadContact.contact_type_id,
    email: leadContact.email.map((email) => ({
      id: email.contact_info_id,
      contactInfoTypeId: email.contact_info_type_id,
      isPrimary: email.is_primary,
      value: email.value,
    })),
    fname: leadContact.fname,
    jobTitle: leadContact.job_title,
    leadId: leadContact.lead_id,
    isActive: leadContact.is_active,
    isPrimary: leadContact.is_primary,
    lname: leadContact.lname,
    mobile: leadContact.mobile.map((mobile) => ({
      id: mobile.contact_info_id,
      contactInfoTypeId: mobile.contact_info_type_id,
      isPrimary: mobile.is_primary,
      countryCode: Number(mobile.prefix),
      countryISO: mobile.region,
      number: mobile.value,
      displayValue: formatContactNumberDisplayValue(Number(mobile.prefix), mobile.value),
      value: formatContactNumber(Number(mobile.prefix), mobile.value),
    })),
  }))
